import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/utils/PageTransition';
import { ScanLine, ArrowUpRight, CheckCircle2, Bot, Factory, ChartBar, Cpu, Workflow, Settings } from 'lucide-react';

const RoboticsAutomation = () => {
  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-20">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <span className="bg-green-50 dark:bg-green-950 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                Robotics Automation
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-6">
                Intelligent <span className="text-primary">Robotics</span> Solutions
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Transforming industries with advanced robotic systems and automation technologies.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
              <div>
                <h2 className="text-2xl font-bold mb-6">Automate Complex Tasks with Intelligent Robots</h2>
                <p className="text-muted-foreground mb-6">
                  Our robotics automation solutions combine artificial intelligence, computer vision, and mechanical engineering 
                  to create intelligent systems that can automate complex tasks with precision and adaptability.
                </p>
                <p className="text-muted-foreground mb-8">
                  From collaborative robots (cobots) that work alongside humans to fully autonomous systems that 
                  operate independently, our solutions are designed to enhance productivity, improve safety, and 
                  reduce operational costs across various industries.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {[
                    'Collaborative Robots', 
                    'Autonomous Mobile Robots', 
                    'Industrial Robotic Systems',
                    'Robotic Process Automation',
                    'Robot Vision Systems',
                    'Human-Robot Collaboration',
                    'Robotic Simulation',
                    'Custom Robotic Solutions'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <Link 
                  to="/contact" 
                  className="glass-button-primary px-6 py-3 text-base font-medium inline-block"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Discuss Your Project
                  <ArrowUpRight className="ml-2 h-4 w-4 inline-block" />
                </Link>
              </div>
              
              <div className="bg-green-50 dark:bg-green-950/30 rounded-2xl p-8">
                <div className="flex items-center mb-6">
                  <Bot className="h-8 w-8 text-green-600 dark:text-green-400 mr-3" />
                  <h3 className="text-xl font-bold">Our Robotics Development Process</h3>
                </div>
                <div className="space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-green-600 dark:text-green-400 font-semibold">1</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Requirements Analysis</h4>
                      <p className="text-sm text-muted-foreground">
                        We analyze your operational needs and identify tasks suitable for robotic automation.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-green-600 dark:text-green-400 font-semibold">2</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">System Design & Simulation</h4>
                      <p className="text-sm text-muted-foreground">
                        We design the robotic system and test it in virtual environments to ensure optimal performance.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-green-600 dark:text-green-400 font-semibold">3</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Integration & Programming</h4>
                      <p className="text-sm text-muted-foreground">
                        We integrate hardware and software components and program the system to perform specific tasks.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="bg-white dark:bg-gray-800 h-10 w-10 rounded-full flex items-center justify-center shadow-md">
                        <span className="text-green-600 dark:text-green-400 font-semibold">4</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Deployment & Optimization</h4>
                      <p className="text-sm text-muted-foreground">
                        We deploy the system in your environment and fine-tune it for maximum efficiency and reliability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-20">
              <h2 className="text-2xl font-bold mb-8 text-center">Industry Applications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  {
                    title: 'Manufacturing & Assembly',
                    icon: <Factory className="h-6 w-6" />,
                    description: 'Automated assembly lines, material handling, and precision manufacturing tasks.'
                  },
                  {
                    title: 'Warehouse & Logistics',
                    icon: <ScanLine className="h-6 w-6" />,
                    description: 'Autonomous mobile robots for inventory management, order fulfillment, and logistics.'
                  },
                  {
                    title: 'Healthcare & Pharmacy',
                    icon: <Bot className="h-6 w-6" />,
                    description: 'Precision robots for surgical assistance, medication dispensing, and lab automation.'
                  },
                  {
                    title: 'Agriculture & Farming',
                    icon: <Settings className="h-6 w-6" />,
                    description: 'Robotic systems for planting, harvesting, sorting, and processing agricultural products.'
                  },
                  {
                    title: 'Quality Control & Inspection',
                    icon: <ChartBar className="h-6 w-6" />,
                    description: 'Automated visual inspection systems for quality assurance across industries.'
                  },
                  {
                    title: 'Research & Development',
                    icon: <Cpu className="h-6 w-6" />,
                    description: 'Custom robotic platforms for scientific research and technology development.'
                  }
                ].map((app, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-xl shadow-subtle p-6 border border-gray-100 dark:border-gray-700">
                    <div className="bg-green-50 dark:bg-green-950/50 p-3 rounded-full w-fit mb-4">
                      {app.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{app.title}</h3>
                    <p className="text-muted-foreground">{app.description}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-2xl p-8 mb-20">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Ready to Automate Your Operations?</h2>
                <p className="text-muted-foreground">
                  Talk to our robotics experts about how automation can transform your business.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/contact" 
                  className="glass-button-primary px-6 py-3 text-center font-medium"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default RoboticsAutomation;
